/**
 * Log Audit (E7.6) — visualizzazione paginata e filtrabile degli eventi di sistema.
 * Accessibile ai ruoli con privilegio VIEW_AUDIT_LOG.
 *
 * Mostra TUTTI gli eventi, compresi quelli dei report ENISA (creazione, promozioni di ticket,
 * note, categoria, CVE, step registrati, risoluzione, rilevazione SLA, campi del modulo ENISA ed
 * export): etichette leggibili, dettaglio prima/dopo espandibile per riga, link al report,
 * filtro per ID entità. I tipi entità del filtro includono quelli presenti nel log
 * (GET /audit/facets).
 */
import { Fragment, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAuditEvents, fetchAuditFacets, type AuditQueryParams } from './audit-api.js';
import { AUDIT_ACTION_LABEL } from './AuditTrail.js';
import {
  KNOWN_ENTITY_TYPES,
  auditEntityHref,
  auditEventDetailRows,
  auditEventKindLabel,
  entityTypeLabel,
  localDateTimeToIso,
} from './auditLabels.js';
import styles from './AuditLogPage.module.css';

const AUDIT_ACTIONS = [
  'create',
  'modify',
  'delete',
  'restore',
  'auth_failure',
  'access_denied',
  'inventory_upload',
  'download',
] as const;

const fmt = new Intl.DateTimeFormat('it-IT', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

function formatTs(ts: string): string {
  try {
    return fmt.format(new Date(ts));
  } catch {
    return ts;
  }
}

function truncate(s: string | undefined, n = 20): string {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// ── MultiSelect ────────────────────────────────────────────────────────────────

interface MultiSelectProps {
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Etichetta leggibile di un'opzione (default: il valore). */
  labelOf?: (opt: string) => string;
  testId?: string;
}

function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Tutte',
  labelOf = (o) => o,
  testId,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const allSelected = value.length === options.length;
  const label =
    value.length === 0 || allSelected
      ? placeholder
      : value.length === 1
        ? labelOf(value[0]!)
        : `${value.length} selezionate`;

  function toggle(opt: string) {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  }

  return (
    <div ref={ref} className={styles.multiSelect} data-testid={testId}>
      <button
        type="button"
        className={`${styles.filterSelect} ${styles.multiSelectTrigger}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={styles.multiSelectLabel}>{label}</span>
        <span className={styles.multiSelectCaret}>▾</span>
      </button>
      {open && (
        <div className={styles.multiSelectPanel}>
          <div className={styles.multiSelectActions}>
            <button
              type="button"
              className={styles.multiSelectActionBtn}
              onClick={() => onChange([...options])}
            >
              Seleziona tutto
            </button>
            <button
              type="button"
              className={styles.multiSelectActionBtn}
              onClick={() => onChange([])}
            >
              Deseleziona tutto
            </button>
          </div>
          <div className={styles.multiSelectOptions}>
            {options.map((opt) => (
              <label key={opt} className={styles.multiSelectOption}>
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() => toggle(opt)}
                  data-testid={testId ? `${testId}-${opt}` : undefined}
                />
                {labelOf(opt)}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

const DEFAULT_FILTERS: AuditQueryParams = {
  actor: '',
  actions: AUDIT_ACTIONS.filter((a) => a !== 'auth_failure'),
  entityTypes: [],
  entityId: '',
  service: '',
  from: '',
  to: '',
  page: 1,
  pageSize: 50,
};

export function AuditLogPage() {
  const [draft, setDraft] = useState<AuditQueryParams>(DEFAULT_FILTERS);
  const [committed, setCommitted] = useState<AuditQueryParams>(DEFAULT_FILTERS);

  const [espansi, setEspansi] = useState<Set<string>>(new Set());

  const { data, isPending, isError } = useQuery({
    queryKey: ['audit', 'events', committed],
    // I campi datetime-local sono in ora locale: al server vanno in ISO UTC.
    queryFn: () =>
      fetchAuditEvents({
        ...committed,
        from: localDateTimeToIso(committed.from),
        to: localDateTimeToIso(committed.to),
      }),
  });
  const { data: facets } = useQuery({
    queryKey: ['audit', 'facets'],
    queryFn: fetchAuditFacets,
    staleTime: 60_000,
  });
  const entityTypes = [...new Set([...KNOWN_ENTITY_TYPES, ...(facets?.entityTypes ?? [])])].sort(
    (a, b) => entityTypeLabel(a).localeCompare(entityTypeLabel(b), 'it'),
  );

  function toggleDettaglio(eventId: string) {
    setEspansi((cur) => {
      const next = new Set(cur);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.pageSize || 50))) : 1;
  const currentPage = data?.page ?? 1;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setCommitted({ ...draft, page: 1 });
  }

  function handleReset() {
    setDraft(DEFAULT_FILTERS);
    setCommitted(DEFAULT_FILTERS);
  }

  function goToPage(p: number) {
    setCommitted((c) => ({ ...c, page: p }));
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Log Audit</h1>
      <p className={styles.subtitle}>
        Registro append-only di tutti gli eventi di sistema — mutazioni, accessi, caricamenti
        inventario.
      </p>

      {/* Filters */}
      <form className={styles.filters} onSubmit={handleSearch}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Attore</label>
          <input
            className={styles.filterInput}
            value={draft.actor ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, actor: e.target.value }))}
            placeholder="es. user@example.com"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Azione</label>
          <MultiSelect
            options={AUDIT_ACTIONS}
            value={draft.actions ?? []}
            onChange={(v) => setDraft((d) => ({ ...d, actions: v }))}
            labelOf={(a) => AUDIT_ACTION_LABEL[a] ?? a}
            testId="audit-filtro-azione"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Tipo entità</label>
          <MultiSelect
            options={entityTypes}
            value={draft.entityTypes ?? []}
            onChange={(v) => setDraft((d) => ({ ...d, entityTypes: v }))}
            labelOf={entityTypeLabel}
            testId="audit-filtro-tipo"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="audit-filtro-entita">
            ID entità
          </label>
          <input
            id="audit-filtro-entita"
            className={styles.filterInput}
            value={draft.entityId ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, entityId: e.target.value.trim() }))}
            placeholder="es. 12 (report ENISA #12)"
            data-testid="audit-filtro-entita"
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Servizio</label>
          <input
            className={styles.filterInput}
            value={draft.service ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, service: e.target.value }))}
            placeholder="es. product-service"
            list="audit-servizi"
          />
          <datalist id="audit-servizi">
            {(facets?.services ?? []).map((sv) => (
              <option key={sv} value={sv} />
            ))}
          </datalist>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Da</label>
          <input
            type="datetime-local"
            className={styles.filterInput}
            value={draft.from ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>A</label>
          <input
            type="datetime-local"
            className={styles.filterInput}
            value={draft.to ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          />
        </div>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.btnPrimary} data-testid="audit-cerca">
            Cerca
          </button>
          <button type="button" className={styles.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>
      </form>

      {isError && <div className={styles.error}>Errore nel caricamento degli eventi audit.</div>}

      {isPending && <div className={styles.empty}>Caricamento…</div>}

      {!isPending && !isError && (
        <>
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Attore</th>
                  <th>Azione</th>
                  <th>Tipo entità</th>
                  <th>ID entità</th>
                  <th>Servizio</th>
                  <th>Correlation ID</th>
                  <th>Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      Nessun evento trovato.
                    </td>
                  </tr>
                ) : (
                  (data?.items ?? []).map((ev) => {
                    const righe = auditEventDetailRows(ev);
                    const aperto = espansi.has(ev.eventId);
                    const sottotipo = auditEventKindLabel(ev);
                    const href = auditEntityHref(ev);
                    return (
                      <Fragment key={ev.eventId}>
                        <tr data-testid={`audit-riga-${ev.eventId}`}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatTs(ev.timestamp)}</td>
                          <td title={ev.actor}>{truncate(ev.actor, 30)}</td>
                          <td>
                            <span
                              className={styles.actionBadge}
                              data-action={ev.action}
                              title={ev.action}
                            >
                              {AUDIT_ACTION_LABEL[ev.action] ?? ev.action}
                            </span>
                            {sottotipo && (
                              <div className={styles.kindLabel} data-testid="audit-sottotipo">
                                {sottotipo}
                              </div>
                            )}
                          </td>
                          <td title={ev.entityType}>{entityTypeLabel(ev.entityType)}</td>
                          <td title={ev.entityId}>
                            {href ? (
                              <Link to={href} data-testid="audit-link-entita">
                                {truncate(ev.entityId, 25)}
                              </Link>
                            ) : (
                              truncate(ev.entityId, 25)
                            )}
                          </td>
                          <td>{ev.service}</td>
                          <td title={ev.correlationId}>{truncate(ev.correlationId, 12)}</td>
                          <td>
                            {righe.length > 0 ? (
                              <button
                                type="button"
                                className={styles.detailToggle}
                                aria-expanded={aperto}
                                onClick={() => toggleDettaglio(ev.eventId)}
                                data-testid="audit-dettaglio-toggle"
                              >
                                {aperto ? 'Nascondi' : `Mostra (${righe.length})`}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                        {aperto && (
                          <tr className={styles.detailRow} data-testid="audit-dettaglio">
                            <td colSpan={8}>
                              <ul className={styles.changes}>
                                {righe.map((r) => (
                                  <li key={r.campo} data-field={r.campo}>
                                    <span className={styles.changeField}>{r.label}:</span>{' '}
                                    {r.prima !== null && !r.invariato ? (
                                      <>
                                        <del>{r.prima}</del> → {r.dopo}
                                      </>
                                    ) : (
                                      r.dopo
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button
              className={styles.btnSecondary}
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              ← Prec
            </button>
            <span>
              Pagina {currentPage} di {totalPages} ({data?.total ?? 0} eventi)
            </span>
            <button
              className={styles.btnPrimary}
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Succ →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
